/**
 * DateTime utility functions for consistent formatting across the application
 */

import moment from 'moment-timezone';

/**
 * Format a datetime string with timezone awareness
 * @param {string} datetime - ISO datetime string
 * @param {string} timezone - Timezone string (e.g., 'America/New_York')
 * @param {string} format - Moment format string (default: 'h:mm A')
 * @returns {string} Formatted datetime string
 */
export function formatDateTime(datetime, timezone, format = 'h:mm A') {
  if (!datetime) return '';
  
  return moment.tz(datetime, timezone).format(format);
}

/**
 * Format time in 12-hour format with AM/PM
 * @param {string} datetime - ISO datetime string
 * @param {string} timezone - Timezone string
 * @returns {string} Time in format "h:mm A" (e.g., "2:30 PM")
 */
export function formatTime(datetime, timezone) {
  return formatDateTime(datetime, timezone, 'h:mm A');
}

/**
 * Format date
 * @param {string} datetime - ISO datetime string
 * @param {string} timezone - Timezone string
 * @param {string} format - Date format (default: 'YYYY-MM-DD')
 * @returns {string} Formatted date
 */
export function formatDate(datetime, timezone, format = 'YYYY-MM-DD') {
  return formatDateTime(datetime, timezone, format);
}

/**
 * Format full datetime with date and time
 * @param {string} datetime - ISO datetime string
 * @param {string} timezone - Timezone string
 * @returns {string} Full formatted datetime (e.g., "Monday, May 15, 2024 at 2:30 PM")
 */
export function formatFullDateTime(datetime, timezone) {
  return formatDateTime(datetime, timezone, 'dddd, MMMM D, YYYY [at] h:mm A');
}

/**
 * Calculate duration between two datetimes
 * @param {string} startDatetime - ISO start datetime string
 * @param {string} endDatetime - ISO end datetime string
 * @param {string} timezone - Timezone string
 * @returns {string} Duration string (e.g., "2h 30m", "45m")
 */
export function calculateDuration(startDatetime, endDatetime, timezone) {
  if (!startDatetime || !endDatetime) return '';
  
  const start = moment.tz(startDatetime, timezone);
  const end = moment.tz(endDatetime, timezone);
  
  const duration = moment.duration(end.diff(start));
  const hours = Math.floor(duration.asHours());
  const minutes = duration.minutes();
  
  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  } else {
    return `${minutes}m`;
  }
}

/**
 * Format datetime range
 * @param {string} startDatetime - ISO start datetime string
 * @param {string} endDatetime - ISO end datetime string
 * @param {string} timezone - Timezone string
 * @returns {string} Formatted range (e.g., "2:30 PM - 4:00 PM")
 */
export function formatDateTimeRange(startDatetime, endDatetime, timezone) {
  if (!startDatetime || !endDatetime) return '';
  
  const start = moment.tz(startDatetime, timezone);
  const end = moment.tz(endDatetime, timezone);
  
  // If same day, just show time range
  if (start.format('YYYY-MM-DD') === end.format('YYYY-MM-DD')) {
    return `${start.format('h:mm A')} - ${end.format('h:mm A')}`;
  } else {
    // Different days, show full datetime for both
    return `${start.format('MMM D, h:mm A')} - ${end.format('MMM D, h:mm A')}`;
  }
}

/**
 * Get relative time (e.g., "in 2 hours", "3 days ago")
 * @param {string} datetime - ISO datetime string
 * @param {string} timezone - Timezone string
 * @returns {string} Relative time string
 */
export function getRelativeTime(datetime, timezone) {
  if (!datetime) return '';
  
  return moment.tz(datetime, timezone).fromNow();
}

/**
 * Check if datetime is in the past
 * @param {string} datetime - ISO datetime string
 * @param {string} timezone - Timezone string
 * @returns {boolean}
 */
export function isPast(datetime, timezone) {
  if (!datetime) return false;
  
  return moment.tz(datetime, timezone).isBefore(moment.tz(timezone));
}

/**
 * Check if datetime is currently happening
 * @param {string} startDatetime - ISO start datetime string
 * @param {string} endDatetime - ISO end datetime string
 * @param {string} timezone - Timezone string
 * @returns {boolean}
 */
export function isHappeningNow(startDatetime, endDatetime, timezone) {
  if (!startDatetime || !endDatetime) return false;
  
  const now = moment.tz(timezone);
  const start = moment.tz(startDatetime, timezone);
  const end = moment.tz(endDatetime, timezone);
  
  return now.isBetween(start, end);
}

/**
 * Check if datetime is upcoming (within next 15 minutes)
 * @param {string} datetime - ISO datetime string
 * @param {string} timezone - Timezone string
 * @param {number} minutesBefore - Minutes before to consider as "upcoming" (default: 15)
 * @returns {boolean}
 */
export function isUpcoming(datetime, timezone, minutesBefore = 15) {
  if (!datetime) return false;
  
  const now = moment.tz(timezone);
  const target = moment.tz(datetime, timezone);
  
  return target.isAfter(now) && target.diff(now, 'minutes') <= minutesBefore;
}

/**
 * Get timezone abbreviation (e.g., "EST", "PDT")
 * @param {string} datetime - ISO datetime string
 * @param {string} timezone - Timezone string
 * @returns {string} Timezone abbreviation
 */
export function getTimezoneAbbreviation(datetime, timezone) {
  if (!datetime || !timezone) return '';
  
  return moment.tz(datetime, timezone).format('z');
}

/**
 * Format with timezone suffix
 * @param {string} datetime - ISO datetime string
 * @param {string} timezone - Timezone string
 * @param {string} format - Moment format string
 * @returns {string} Formatted datetime with timezone abbreviation
 */
export function formatWithTimezone(datetime, timezone, format = 'h:mm A') {
  if (!datetime) return '';
  
  const formatted = formatDateTime(datetime, timezone, format);
  const tzAbbr = getTimezoneAbbreviation(datetime, timezone);
  
  return `${formatted} ${tzAbbr}`;
}